import { call, put, takeLatest } from 'redux-saga/effects';

import { history } from '@utils/history';
import { CorporateHealthQuote } from '@models/CorporateHealthQuote';
import { CorporateHealthQuoteApi } from '@apis/CorporateHealthQuoteApi';
import { CorporateHealthOrderApi } from '@apis/CorporateHealthOrderApi';
import { SagaPayload } from '@utils/typescript';
import { CorporateUser } from '@models/User';
import { Pagination } from '@models/Pagination';
import { ImportModel } from '@models/ImportModel';
import { CorporateHealthOrderUserRemainingBenefit } from '@models/CorporateHealthOrderUserRemainingBenefit';

import {
  corporateHealthQuoteCreate,
  CorporateHealthQuoteCreatePayload,
  corporateHealthQuoteFetch,
  CorporateHealthQuoteFetchPayload,
  corporateHealthQuoteUpdate,
  CorporateHealthQuoteUpdatePayload,
  corporateHealthQuoteLock,
  CorporateHealthQuoteLockPayload,
  corporateHealthQuotePayByCash,
  CorporateHealthQuotePayByCashPayload,
  corporateHealthQuoteGetUsers,
  CorporateHealthQuoteGetUsersPayload,
  corporateHealthQuoteGetUsersSuccess,
  corporateHealthQuoteUpdateUser,
  CorporateHealthQuoteUpdateUserPayload,
  corporateHealthQuoteUpdateUserSuccess,
  corporateHealthQuoteDeleteUser,
  corporateHealthQuoteDeleteUserSuccess,
  CorporateHealthQuoteDeleteUserPayload,
  corporateHealthQuoteGetImports,
  CorporateHealthQuoteGetImportsPayload,
  corporateHealthQuoteCreateImport,
  CorporateHealthQuoteCreateImportPayload,
  corporateHealthQuoteCreateImportSuccess,
  corporateHealthQuoteExportActiveUsers,
  CorporateHealthQuoteExportActiveUsersPayload,
  stateUpdate,
  StateUpdatePayload,
  corporateHealthOrderUserRemainingBenefitsFetch,
  CorporateHealthOrderUserRemainingBenefitsFetchPayload,
} from './action';
import { routeCorporateHealthQuoteBuyReviewPage } from './route';

function* handleCorporateHealthQuoteCreate({ payload }: SagaPayload<CorporateHealthQuoteCreatePayload>) {
  const state: StateUpdatePayload = { corporateHealthQuote: null };
  try {
    state.corporateHealthQuote = (yield call(CorporateHealthQuoteApi.create, payload)) as CorporateHealthQuote;
    yield put(stateUpdate(state));
    history.navigate(routeCorporateHealthQuoteBuyReviewPage({ orderNumber: state.corporateHealthQuote.orderNumber }));
  } catch { }
}

function* handleCorporateHealthQuoteFetch({ payload }: SagaPayload<CorporateHealthQuoteFetchPayload>) {
  const state: StateUpdatePayload = { corporateHealthQuote: null };
  try {
    state.corporateHealthQuote = (yield call(CorporateHealthQuoteApi.get, payload)) as CorporateHealthQuote;
    yield put(stateUpdate(state));
  } catch {
    yield put(stateUpdate(state));
  }
}

function* handleCorporateHealthQuoteUpdate({ payload }: SagaPayload<CorporateHealthQuoteUpdatePayload>) {
  const state: StateUpdatePayload = { corporateHealthQuote: null };
  try {
    state.corporateHealthQuote = (yield call(CorporateHealthQuoteApi.update, payload)) as CorporateHealthQuote;
    yield put(stateUpdate(state));
    history.navigate(routeCorporateHealthQuoteBuyReviewPage({ orderNumber: payload.orderNumber }));
  } catch {
    yield put(stateUpdate(state));
  }
}

function* handleCorporateHealthQuoteLock({ payload }: SagaPayload<CorporateHealthQuoteLockPayload>) {
  try {
    yield call(CorporateHealthQuoteApi.lock, payload);
    history.navigate(routeCorporateHealthQuoteBuyReviewPage({ orderNumber: payload.orderNumber }));
  } catch { }
}

function* handleCorporateHealthQuotePayByCash({ payload }: SagaPayload<CorporateHealthQuotePayByCashPayload>) {
  try {
    yield call(CorporateHealthQuoteApi.payByCash, payload);
    history.navigate(routeCorporateHealthQuoteBuyReviewPage({ orderNumber: payload.orderNumber }));
  } catch { }
}

function* handleCorporateHealthQuoteGetUsers({ payload }: SagaPayload<CorporateHealthQuoteGetUsersPayload>) {
  const state: StateUpdatePayload = { isUsersLoading: true };
  try {
    yield put(stateUpdate(state));
    const [users, usersPagination] = (yield call(CorporateHealthQuoteApi.getUsers, payload)) as [CorporateUser[], Pagination];
    yield put(corporateHealthQuoteGetUsersSuccess({ users, usersPagination }));
  } catch { }
}

function* handleCorporateHealthQuoteUpdateUser({ payload }: SagaPayload<CorporateHealthQuoteUpdateUserPayload>) {
  try {
    const user = (yield call(CorporateHealthQuoteApi.updateUser, payload)) as CorporateUser;
    yield put(corporateHealthQuoteUpdateUserSuccess(user));
  } catch { }
}

function* handleCorporateHealthQuoteDeleteUser({ payload }: SagaPayload<CorporateHealthQuoteDeleteUserPayload>) {
  try {
    yield call(CorporateHealthQuoteApi.deleteUser, payload);
    yield put(corporateHealthQuoteDeleteUserSuccess(payload.userId));
  } catch { }
}

function* handleCorporateHealthQuoteGetImports({ payload }: SagaPayload<CorporateHealthQuoteGetImportsPayload>) {
  const state: StateUpdatePayload = {};
  try {
    state.imports = (yield call(CorporateHealthQuoteApi.getImports, payload)) as ImportModel[];
    yield put(stateUpdate(state));
  } catch { }
}

function* handleCorporateHealthQuoteCreateImport({ payload }: SagaPayload<CorporateHealthQuoteCreateImportPayload>) {
  try {
    const newImport = (yield call(CorporateHealthQuoteApi.createImport, payload)) as ImportModel;
    yield put(corporateHealthQuoteCreateImportSuccess(newImport))
  } catch { }
}

function* handleCorporateHealthQuoteExportActiveUsers({ payload }: SagaPayload<CorporateHealthQuoteExportActiveUsersPayload>) {
  try {
    yield call(CorporateHealthQuoteApi.ExportActiveUsers, payload)
  } catch { }
}

function* handleCorporateHealthOrderUserRemainingBenefitsFetch({ payload }: SagaPayload<CorporateHealthOrderUserRemainingBenefitsFetchPayload>) {
  const state: StateUpdatePayload = { corporateHealthOrderUserRemainingBenefits: null };
  try {
    state.corporateHealthOrderUserRemainingBenefits = (yield call(CorporateHealthOrderApi.getCorporateHealthOrderUserRemainingBenefits, payload)) as CorporateHealthOrderUserRemainingBenefit[];
    yield put(stateUpdate(state));
  } catch { }
}

export default function* () {
  yield takeLatest(corporateHealthQuoteCreate, handleCorporateHealthQuoteCreate);
  yield takeLatest(corporateHealthQuoteFetch, handleCorporateHealthQuoteFetch);
  yield takeLatest(corporateHealthQuoteUpdate, handleCorporateHealthQuoteUpdate);
  yield takeLatest(corporateHealthQuoteLock, handleCorporateHealthQuoteLock);
  yield takeLatest(corporateHealthQuotePayByCash, handleCorporateHealthQuotePayByCash);
  yield takeLatest(corporateHealthQuoteGetUsers, handleCorporateHealthQuoteGetUsers);
  yield takeLatest(corporateHealthQuoteUpdateUser, handleCorporateHealthQuoteUpdateUser);
  yield takeLatest(corporateHealthQuoteDeleteUser, handleCorporateHealthQuoteDeleteUser);
  yield takeLatest(corporateHealthQuoteGetImports, handleCorporateHealthQuoteGetImports);
  yield takeLatest(corporateHealthQuoteCreateImport, handleCorporateHealthQuoteCreateImport);
  yield takeLatest(corporateHealthQuoteExportActiveUsers, handleCorporateHealthQuoteExportActiveUsers);
  yield takeLatest(corporateHealthOrderUserRemainingBenefitsFetch, handleCorporateHealthOrderUserRemainingBenefitsFetch);
}
